import { useRef, useState } from "react"
import { UploadCloudIcon } from "lucide-react"

interface UploadFileProps {
    id?: string
    inputId?: string
    disabled?: boolean
    file: File | File[] | null
    handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    acceptedFormats: string
    supportedFormats?: string
    multiple?: boolean
}

export default function UploadFile({
    file,
    handleFileChange,
    acceptedFormats,
    supportedFormats,
    multiple,
    id,
    inputId,
    disabled = false,
}: UploadFileProps) {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [isDragging, setIsDragging] = useState(false)
    const files = Array.isArray(file) ? file : file ? [file] : []
    const totalSizeKb = files.reduce((sum, f) => sum + f.size, 0) / 1024

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        setIsDragging(false)
        if (disabled) return
        const droppedFiles = event.dataTransfer?.files
        if (!droppedFiles || droppedFiles.length === 0) return

        // 构造一个最小的 ChangeEvent，复用原有回调
        const syntheticEvent = {
            target: { files: droppedFiles } as unknown as HTMLInputElement,
            currentTarget: { files: droppedFiles } as unknown as HTMLInputElement,
        } as React.ChangeEvent<HTMLInputElement>

        handleFileChange(syntheticEvent)
    }

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        setIsDragging(true)
    }

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        setIsDragging(false)
    }
  return (

      <div id={id} className='grid gap-4'>
          <div
              className={`group cursor-pointer rounded-md border-2 border-dashed p-8 text-center transition-colors hover:border-primary hover:bg-primary/5 ${isDragging ? 'border-primary/60 bg-primary/5' : files.length > 0 ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
              role='button'
              tabIndex={disabled ? -1 : 0}
              aria-disabled={disabled}
              aria-label='Select files'
              onClick={() => !disabled && fileInputRef.current?.click()}
              onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return
                  if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
                      event.preventDefault()
                      fileInputRef.current?.click()
                  }
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
          >
              <input
                  type='file'
                  id={inputId}
                  disabled={disabled}
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className='hidden'
                  accept={acceptedFormats}
                  multiple={multiple}
              />
              <UploadCloudIcon className='text-muted-foreground mx-auto mb-4 block h-8 w-8 transition-colors group-hover:text-primary' />
              {files.length > 0 ? (
                  <div>
                      <p className='text-sm font-medium'>
                          {files.length === 1 ? files[0].name : `${files.length} files selected`}
                      </p>
                      {files.length > 1 && (
                          <div className='text-muted-foreground mt-2 text-xs'>
                              {files.slice(0, 5).map((f) => (
                                  <div key={`${f.name}-${f.size}`}>{f.name}</div>
                              ))}
                              {files.length > 5 && <div>...and {files.length - 5} more</div>}
                          </div>
                      )}
                      <p className='text-muted-foreground mt-1 text-xs'>
                          {totalSizeKb.toFixed(2)} KB total
                      </p>
                  </div>
              ) : (
                  <>
                      <p className='text-sm font-medium'>
                          Drag & drop a file here, or click to select
                      </p>
                      <p className='text-muted-foreground mt-1 text-xs'>
                          Supported formats: {supportedFormats||'any'}
                      </p>
                  </>
              )}
          </div>
      </div>
  )
}
